import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import { Button, TextField, Paper, FormControl, InputLabel, Select, FormControlLabel, Switch } from '@material-ui/core';
import { format } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import KanbanBoard from './KanbanBoard';

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(1),
    overflowX: 'auto',
    ...theme.scrollbarStyles,
    borderRadius: '10px',
  },
  button: {
    borderRadius: '10px',
  },
  dateInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    marginRight: theme.spacing(1),
  },
  sortSelect: {
    minWidth: 150,
    marginRight: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      backgroundColor: theme.palette.background.paper,
      paddingLeft: 4,
      paddingRight: 4,
    },
  },
  switchLabel: {
    marginRight: theme.spacing(1),
  },
  filterSelect: {
    minWidth: 200,
    marginRight: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      backgroundColor: theme.palette.background.paper,
      paddingLeft: 4,
      paddingRight: 4,
    },
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [filterUserId, setFilterUserId] = useState('');
  const [filterConnectionId, setFilterConnectionId] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [connectionsList, setConnectionsList] = useState([]);
  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);
  const [showClosed, setShowClosed] = useState(false);

  const canSeeOtherUsersChats = user?.profile === 'admin' || user?.allUserChat === 'enabled';
  const usersListForFilter = useMemo(() => {
    if (canSeeOtherUsersChats) return usersList;
    const onlyMe = usersList.filter(u => Number(u.id) === Number(user?.id));
    return onlyMe.length > 0 ? onlyMe : (user?.id && user?.name ? [{ id: user.id, name: user.name }] : []);
  }, [canSeeOtherUsersChats, usersList, user?.id, user?.name]);

  useEffect(() => {
    if (filterUserId && !usersListForFilter.some(u => Number(u.id) === Number(filterUserId))) {
      setFilterUserId('');
    }
  }, [usersListForFilter, filterUserId]);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Carregar ordem das lanes do banco de dados
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };

    if (user && user.id) {
      loadLaneOrder();
    }
  }, [user]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadUsersAndConnections = async () => {
      try {
        const [usersRes, whatsappRes] = await Promise.all([
          api.get('/users/list'),
          api.get('/whatsapp/'),
        ]);
        const list = usersRes.data && Array.isArray(usersRes.data) ? usersRes.data : [];
        setUsersList(list.map(u => ({ id: u.id, name: u.name })));
        const conns = whatsappRes.data && Array.isArray(whatsappRes.data) ? whatsappRes.data : [];
        const companyConnections = conns.filter(c => c.companyId === user.companyId);
        setConnectionsList(companyConnections.map(c => ({ id: c.id, name: c.name })));
      } catch (err) {
        console.error(err);
      }
    };
    if (user && user.companyId) loadUsersAndConnections();
  }, [user]);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

  // Limite de páginas para evitar sobrecarga (ex.: 15 páginas = 6000 tickets)
  const MAX_KANBAN_PAGES = 15;

  const fetchTickets = async (fetchedTags = tags) => {
    try {
      const params = {
        queueIds: JSON.stringify(queueIds),
      };
      // Só aplicar filtro de data quando estiver vendo fechados (senão carrega todos os abertos, qualquer data)
      if (showClosed) {
        params.startDate = startDate;
        params.endDate = endDate;
        params.dateStart = startDate;
        params.dateEnd = endDate;
        params.showClosed = 'true';
      }
      if (filterUserId) params.users = JSON.stringify([Number(filterUserId)]);
      if (filterConnectionId) params.whatsapps = JSON.stringify([Number(filterConnectionId)]);

      let allTickets = [];
      let pageNumber = 1;
      let hasMore = true;

      while (hasMore && pageNumber <= MAX_KANBAN_PAGES) {
        const { data } = await api.get('/ticket/kanban', { params: { ...params, pageNumber: String(pageNumber) } });
        allTickets = allTickets.concat(data.tickets || []);
        hasMore = data.hasMore === true && (data.tickets || []).length > 0;
        pageNumber += 1;
      }

      setTickets(allTickets);
      organizeLanes(fetchedTags, allTickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  const debounceRef = useRef(null);

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (!['create', 'update', 'delete'].includes(data.action)) return;
      // Debounce: evita múltiplos reloads seguidos (ex.: vários tickets atualizados em sequência)
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        fetchTickets();
      }, 2000);
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = updatedTicket => {
    setTickets(prevTickets =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
  };

  const onTicketClosed = ticketId => {
    setTickets(prevTickets => prevTickets.filter(t => t.id !== ticketId));
  };

  const getOpportunityValue = (ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return opportunityValue;
  };

  const organizeLanes = (fetchedTags = tags, fetchedTickets = tickets) => {
    const sortedTickets = [...fetchedTickets];

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }

    const defaultTickets = sortedTickets.filter(
      ticket => ticket.tags.length === 0
    );

    const lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...fetchedTags.map(tag => {
        const taggedTickets = sortedTickets.filter(ticket =>
          ticket.tags.some(t => t.id === tag.id)
        );
        return {
          id: tag.id.toString(),
          title: tag.name,
          tickets: taggedTickets,
          color: tag.color || '#757575',
        };
      }),
    ];

    // Aplicar ordem personalizada se existir
    if (laneOrder && laneOrder.length > 0) {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      
      // Adicionar lanes na ordem salva
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      
      // Adicionar lanes restantes (novas lanes que não estavam na ordem salva)
      laneMap.forEach(lane => orderedLanes.push(lane));
      
      setLanes(orderedLanes);
    } else {
      setLanes(lanesData);
    }
  };

  useEffect(() => {
    if (!loadingLaneOrder) {
      organizeLanes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, tickets, sortOrder, laneOrder, loadingLaneOrder]);

  const handleCardMove = async (ticketId, targetLaneId) => {
    ticketId = parseInt(ticketId, 10);
    try {
      await api.delete(`/ticket-tags/${ticketId}`);

      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
        toast.success('Ticket Tag atualizado com sucesso!');
      } else {
        toast.success('Ticket Tag removido!');
      }

      fetchTickets();
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    // Verificar se o usuário é admin
    if (user.profile !== 'admin') {
      toast.error('Apenas administradores podem reordenar as lanes do Kanban');
      return;
    }

    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    
    setLanes(newLanes);
    
    // Salvar nova ordem no banco de dados
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    
    try {
      await setKanbanLaneOrder(newLaneOrder);
      toast.success('Ordem das lanes atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao salvar ordem das lanes:', error);
      toast.error('Erro ao salvar ordem das lanes');
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t('Kanban')}</Title>
        <MainHeaderButtonsWrapper>
          <FormControl variant="outlined" size="small" className={classes.filterSelect}>
            <InputLabel htmlFor="filter-user-select" shrink>{i18n.t('kanban.filterByUser')}</InputLabel>
            <Select
              native
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              label={i18n.t('kanban.filterByUser')}
              inputProps={{ name: 'filterUser', id: 'filter-user-select' }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">{i18n.t('kanban.filterAll')}</option>
              {usersListForFilter.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterSelect}>
            <InputLabel htmlFor="filter-connection-select" shrink>{i18n.t('kanban.filterByConnection')}</InputLabel>
            <Select
              native
              value={filterConnectionId}
              onChange={(e) => setFilterConnectionId(e.target.value)}
              label={i18n.t('kanban.filterByConnection')}
              inputProps={{ name: 'filterConnection', id: 'filter-connection-select' }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">{i18n.t('kanban.filterAll')}</option>
              {connectionsList.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.sortSelect}
          >
            <InputLabel htmlFor="sort-order-select" shrink>Ordenar por</InputLabel>
            <Select
              native
              value={sortOrder}
              onChange={handleSortOrderChange}
              label="Ordenar por"
              inputProps={{
                name: 'sortOrder',
                id: 'sort-order-select',
              }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="ticketNumber">Número do Ticket</option>
              <option value="lastMessageTime">Última Mensagem</option>
              <option value="valorDesc">Valor (maior para menor)</option>
            </Select>
          </FormControl>
          <TextField
            label="Data de início"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <TextField
            label="Data de fim"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showClosed}
                onChange={(e) => setShowClosed(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={i18n.t('kanban.showClosed')}
            className={classes.switchLabel}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearchClick}
            className={classes.button}
          >
            Buscar
          </Button>
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddColumnClick}
                className={classes.button}
              >
                + Adicionar colunas
              </Button>
            )}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined" className={classes.mainPaper}>
        <KanbanBoard
          lanes={lanes}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          onTicketClosed={onTicketClosed}
          onTicketTransferred={fetchTickets}
          isAdmin={user.profile === 'admin'}
        />
      </Paper>
    </MainContainer>
  );
};

export default Kanban;
